import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Info from '@mui/icons-material/Info';
import type { SelectChangeEvent } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDocumentExpiryDate } from 'hooks/useDocumentExpiryDate';
import { useExpiryDateCalendar } from './useExpiryDateCalendar';
import { useUpdateDocumentExpiryDate } from '../../../../hooks/useUpdateDocumentExpiryDate.ts';

type ExpiryDateOptions = 'never' | 'custom';

interface DocumentExpiryDateSelectorProps {
  contentId: string;
}

export const DocumentExpiryDateSelector: React.FC<DocumentExpiryDateSelectorProps> = ({ contentId }) => {
  const { t } = useTranslation();
  const [shouldLinkExpire, setShouldLinkExpire] = useState<ExpiryDateOptions>('never');
  const { getDocumentExpiryDate } = useDocumentExpiryDate({ contentId });
  const { data: documentExpiryDate, isLoading: isLoadingDocumentExpiryDate } = getDocumentExpiryDate;
  const currentExpiryAtDate = useMemo(() => dayjs(documentExpiryDate?.expire_at), [documentExpiryDate?.expire_at]);
  const { updateDocumentExpiryDate } = useUpdateDocumentExpiryDate({
    contentId,
    expiryDate: currentExpiryAtDate.isValid() ? currentExpiryAtDate : null,
  });

  const { open: openCalendar, modal } = useExpiryDateCalendar({ contentId, currentExpiryAtDate });

  useEffect(() => {
    if (currentExpiryAtDate.isValid()) {
      setShouldLinkExpire('custom');
    } else {
      setShouldLinkExpire('never');
    }
  }, [currentExpiryAtDate]);

  const handleChange = (event: SelectChangeEvent<ExpiryDateOptions>) => {
    const value = event.target.value as ExpiryDateOptions;
    setShouldLinkExpire(value);
    if (value === 'custom') {
      openCalendar();
    } else {
      updateDocumentExpiryDate.mutate(null);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center">
          <Typography variant="h5" fontSize="16px" fontWeight={400}>
            {t('share_document_v3.share_card.link_expiry')}
          </Typography>
          <Tooltip title={t('share_document_v3.share_card.link_expiry_tooltip')} placement="bottom" arrow>
            <Info
              sx={{ fontSize: 20, color: 'text.secondary', ml: 1, cursor: 'pointer' }}
              data-testid="document-expiry-date-selector-info"
            />
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} data-testid="document-expiry-date-selector">
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <InputLabel id="document-expiry-date-selector">{t('share_document_v3.share_card.link_expiry_selector_label')}</InputLabel>
            {isLoadingDocumentExpiryDate ? (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                justifyContent="center"
                border="1px solid #E0E0E0"
                borderRadius={1}
                p={1}
                height={56}
              >
                <CircularProgress size={20} />
              </Stack>
            ) : (
              <Select
                labelId="document-expiry-date-selector"
                id="document-expiry-date-selector"
                value={shouldLinkExpire}
                label={t('share_document_v3.share_card.link_expiry_selector_label')}
                onChange={handleChange}
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value="never" data-testid="document-expiry-date-selector-never">
                  {t('share_document_v3.calendar_modal.option_never')}
                </MenuItem>
                <MenuItem value="custom" data-testid="document-expiry-date-selector-custom" onClick={openCalendar}>
                  {currentExpiryAtDate.isValid()
                    ? currentExpiryAtDate.format('DD/MM/YYYY')
                    : t('share_document_v3.calendar_modal.option_custom')}
                </MenuItem>
              </Select>
            )}
          </FormControl>
        </Stack>
      </Stack>
      {modal}
    </>
  );
};
